import React, { ReactElement, useEffect } from 'react'
import { Link } from 'react-router'

import paths from '../../constants/paths'
import MfaChannel from '../../types/mfa-channel'
import { FormComponentProps, withValidations } from '../../utils/form-utils'
import { formatMfaChannel } from '../../utils/format-utils'
import { getNewFrontendRedirectUrl, NEW_USER_SIGNUP_REDIRECT_ENABLED } from '../../utils/new-frontend-redirect'
import { t, tx } from '../../utils/translation-utils'
import Form from '../antd/form'
import Button from '../elements/button'
import Checkbox from '../elements/checkbox'
import Input from '../elements/input'
import DumbLink from '../widgets/DumbLink'

type Props = {
  email?: string
  channel?: MfaChannel
  remember?: boolean
}

export type Fields = {
  email?: string
  password?: string
  response?: string
  recoveryCode?: string
  trustDevice?: boolean
  useRecoveryCode: boolean
  remember: boolean
}

function LoginForm(props: Props & FormComponentProps<Fields, Fields>): ReactElement | null {
  const newFrontendUrl = getNewFrontendRedirectUrl()

  const toggleRecoveryCode = (e: React.MouseEvent) => {
    e.preventDefault()

    props.setFieldValue('useRecoveryCode', true)
  }

  const { decorateField, getFieldValue } = props

  //if using email mfa always check trust device for 30 days
  useEffect(() => {
    let isActive = true

    if (props.channel && formatMfaChannel(props.channel) === 'E-mail') {
      if (isActive) {
        props.setFieldValue('trustDevice', true)
      }
    }
    return () => {
      isActive = false // Cleanup function
    }
  }, [props.channel])

  if (props.channel) {
    const useRecoveryCode = getFieldValue('useRecoveryCode')
    let desc
    if (useRecoveryCode) {
      desc = t('login.channel.recovery_code')
    } else {
      desc = t('login.channel.mfa_channel', { channel: formatMfaChannel(props.channel) })
    }

    return (
      <div>
        {formatMfaChannel(props.channel) === 'E-mail' && (
          <p style={{ textAlign: 'left' }}>{t('login.channel.description')}</p>
        )}
        <p style={{ textAlign: 'left', marginTop: '.8em' }}>{t('login.channel.title', { desc })}</p>
        <p>&nbsp;</p>
        {!useRecoveryCode &&
          decorateField('response', {
            placeholder: t('login.channel.form.response'),
            validate: (val) => {
              if (!val) {
                return t('login.channel.form.response.required')
              }
              return null
            },
          })(<Input inputMode="numeric" autoComplete="one-time-code" />)}
        {useRecoveryCode &&
          decorateField('recoveryCode', {
            placeholder: t('login.channel.form.recovery_code'),
            validate: (val) => {
              if (!val) {
                return t('login.channel.form.recovery_code.required')
              }
              return null
            },
          })(<Input inputMode="numeric" />)}
        <Form.Item>
          <DumbLink onClick={toggleRecoveryCode} className="login-form-forgot">
            {t('login.channel.form.toggle_recovery_code')}
          </DumbLink>
          {decorateField('trustDevice', {
            skipWrapper: true,
            skipLabel: true,
            valueOnChecked: true,
          })(<Checkbox>{t('login.channel.form.trust_device')}</Checkbox>)}

          <Button htmlType="submit" type="primary" id="button-log-in">
            Log ind
          </Button>
        </Form.Item>
      </div>
    )
  }
  return (
    <div>
      {decorateField('email', {
        placeholder: t('login.form.email'),
        validate: (val) => {
          if (!val) {
            return t('login.form.email.required')
          }
          return null
        },
      })(<Input />)}
      {decorateField('password', {
        placeholder: t('login.form.password'),
        validate: (val) => (!val ? t('login.form.password.required') : null),
      })(<Input type="password" />)}
      <Form.Item>
        <Link to={'/' + paths.REQUEST_PASSWORD} className="login-form-forgot">
          {t('login.form.forgot_password')}
        </Link>
        {decorateField('remember', {
          skipWrapper: true,
          skipLabel: true,
          valueOnChecked: true,
        })(<Checkbox>{t('login.form.remember')}</Checkbox>)}

        <Button htmlType="submit" type="primary" id="button-log-in">
          {t('login.form.submit')}
        </Button>

        {/* TEMPORARY (new-frontend pilot): send new signups to the new frontend when
            enabled and the current host maps to one. Flip NEW_USER_SIGNUP_REDIRECT_ENABLED
            in new-frontend-redirect.ts to false to revert instantly. */}
        {NEW_USER_SIGNUP_REDIRECT_ENABLED && newFrontendUrl ? (
          <a href={newFrontendUrl} className="no-margin-link">
            <Button className="login-form-button" type="secondary">
              {t('login.form.create_new_user')}
            </Button>
          </a>
        ) : (
          <Link to={'/' + paths.REGISTER + document.location.search} className="no-margin-link">
            <Button className="login-form-button" type="secondary">
              {t('login.form.create_new_user')}
            </Button>
          </Link>
        )}

        <p>
          {tx('login.form.employee_user_note', {
            link: (
              <a href="https://employee.salary.dk" target="_blank" rel="noopener noreferrer">
                {t('login.form.employee_user_note.link')}
              </a>
            ),
          })}
        </p>
      </Form.Item>
    </div>
  )
}

export default withValidations<Props, Fields, Fields>({
  mapPropsToFields: (props) => {
    const fields: Fields = { useRecoveryCode: false, remember: false }
    if (props.email) {
      fields.email = props.email
    }
    if (props.remember) {
      fields.remember = props.remember
    }
    return fields
  },
  onSubmit: (values) => values,
})(LoginForm)
